import React, { FC, useCallback, useEffect } from 'react';
import { useTranslation } from '@shared/core';
import { useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { PhotoUploaderProps, PhotoUploaderWithPollingProps } from './PhotoUploader.types';
import { useAddMediaToCollectionV2Mutation } from '@graphql/generated';
import { PhotoPickerDialog } from '@apps/admin/components/PhotoPickerDialog/PhotoPickerDialog';
import { CheckIcon } from '@assets/icons';
import { addAction } from '@shared/utils/logger';
import { AddMediaArgs, PICKER_TYPES } from '@apps/admin/components/PhotoPickerDialog/PhotoPickerDialog.types';
import { usePhotoUpload } from '@shared/components/PhotoUploader/hooks/usePhotoUpload';
import i18n from '@shared/core/i18n/i18n';

const PhotoUploader: FC<PhotoUploaderProps> = props => {
  const {
    collectionId,
    isFetching = false,
    media,
    isOpenPickerDialog,
    onClosePickerDialog,
    handleOpenFilePicker,
    uploadInProgressCount = 0,
    showCount = false,
    pickerType = PICKER_TYPES.ADD_TO_COLLECTION,
    onPhotoSelect = () => {},
    maxCount,
    minCount,
    photoSelectionHelperText,
    selectedItems = [],
    disabledItems = [],
    supportsPhotoCrop,
    photoCropData,
    defaultTab,
    enableMobileCrop = false,
  } = props;

  const { tq } = useTranslation('photoUploader');
  const { toast } = useToast();

  useEffect(() => {
    // preload translation namespaces to avoid flickering when the dialog is opened
    i18n.loadNamespaces(['photoPickerDialog', 'multiPhotoSelector']);
  }, []);

  const [addMediaToCollectionV2, { loading: isAddingMediaToCollectionV2 }] = useAddMediaToCollectionV2Mutation();

  const handleAddMediaToCollection = useCallback(
    async (selectedItemsId: ReadonlyArray<string>, cbOnSuccess: () => void) => {
      if (!collectionId) {
        return;
      }
      await addMediaToCollectionV2({
        variables: {
          payload: {
            collectionId,
            mediaIds: [...selectedItemsId],
          },
        },
        refetchQueries: ['MediaCollections', 'MediaCollectionById'],
        awaitRefetchQueries: true,
      }).then(response => {
        if (!response.data?.addMediaToCollectionV2.success) {
          if (
            response.data?.addMediaToCollectionV2.__typename === 'addMediaToCollectionResponseError' &&
            response.data.addMediaToCollectionV2.failedMediaIds?.length === selectedItemsId.length
          ) {
            // All failed case
            toast(tq`An error occurred while adding photos to your album. Please try again.`, { icon: <WarningTriangle /> });
            addAction('addMediaToCollectionV2', { status: 'failed', failedIds: response.data.addMediaToCollectionV2.failedMediaIds || [] });
          } else {
            // Partial success case
            toast(tq`Some of the items could not be added. Please check the album and try again.`, { icon: <WarningTriangle /> });
            const failedIds =
              (response.data?.addMediaToCollectionV2.__typename === 'addMediaToCollectionResponseError' && response.data.addMediaToCollectionV2.failedMediaIds) || [];
            addAction('addMediaToCollectionV2', { status: 'partialSuccess', failedIds });
            onClosePickerDialog();
            cbOnSuccess();
          }
        } else {
          // All success case
          toast(tq`Photos added to album.`, { icon: <CheckIcon /> });
          addAction('addMediaToCollectionV2', { status: 'success' });
          onClosePickerDialog();
          cbOnSuccess();
        }
      });
    },
    [addMediaToCollectionV2, collectionId, onClosePickerDialog, toast, tq],
  );

  const handleAddMedia = useCallback(
    (args: AddMediaArgs) => {
      const { selectedItemsId, cropData, alignmentData, mobileCropData, cb } = args;

      if (pickerType === PICKER_TYPES.ADD_TO_COLLECTION) {
        handleAddMediaToCollection(selectedItemsId, cb);
      } else {
        onPhotoSelect({ selectedItemsId, cropData, alignmentData, mobileCropData, cb });
      }
    },
    [handleAddMediaToCollection, onPhotoSelect, pickerType],
  );

  return (
    <PhotoPickerDialog
      isOpen={isOpenPickerDialog}
      handleClose={onClosePickerDialog}
      handleOpenFilePicker={handleOpenFilePicker}
      isFetching={isFetching}
      media={media}
      handleAddMedia={handleAddMedia}
      isAddingMedia={isAddingMediaToCollectionV2}
      collectionId={collectionId}
      showCount={showCount}
      pickerType={pickerType}
      maxCount={maxCount}
      minCount={minCount}
      photoSelectionHelperText={photoSelectionHelperText}
      selectedItems={selectedItems}
      disabledItems={disabledItems}
      uploadInProgressCount={uploadInProgressCount}
      supportsPhotoCrop={supportsPhotoCrop}
      photoCropData={photoCropData}
      defaultTab={defaultTab}
      enableMobileCrop={enableMobileCrop}
    />
  );
};

PhotoUploader.displayName = 'PhotoUploader';

const PhotoUploaderWithPolling: FC<PhotoUploaderWithPollingProps> = props => {
  const { handleOpenFilePicker, fileUploadStatusData } = usePhotoUpload({
    eventId: props.eventId,
    collectionId: props.collectionId,
    containerId: props.containerId,
    updateUploadStatus: props.updateUploadStatus,
    showPhotoLibrary: props.showPhotoLibrary ?? true,
    onOpenPickerDialog: props.onOpenPickerDialog,
    refetchQueries: ['MediaCollections', 'MediaCollectionById', 'GetEventMedia'],
    useCase: props.useCase,
  });

  return (
    <PhotoUploader
      {...props}
      handleOpenFilePicker={handleOpenFilePicker}
      uploadInProgressCount={fileUploadStatusData.isUploading ? fileUploadStatusData.imageCount : 0}
    />
  );
};

export { PhotoUploaderWithPolling as PhotoUploader, PhotoUploader as PhotoUploaderWithoutPolling };
